import React, { FormEventHandler, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { FrameworkSectionsEditor } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor'
import {
  getDuplicateNameErrors,
  getEmptyNameErrors,
  parseBackendDuplicateError,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.helpers'
import {
  FrameworkSectionsEditorErrors,
  SectionDraft,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'
import {
  useFrameworkControls,
  useSaveFrameworkSections,
} from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import type { SectionEditSelection } from 'src/pages/Frameworks/show/frameworksShowSections.types'
import { ModalForm, ModalFormId } from '../..'

type Props = {
  framework: ComplianceFramework
  selectedSection: SectionEditSelection | null
  clearSelectedSection: () => void
}

export const EditFrameworkSectionModal = ({
  framework,
  selectedSection,
  clearSelectedSection,
}: Props): JSX.Element => {
  const show = useIsShowModal(ModalFormId.EDIT_FRAMEWORK_SECTION)
  const hideModal = useHideModal({
    modalId: ModalFormId.EDIT_FRAMEWORK_SECTION,
  })
  const saveSections = useSaveFrameworkSections()
  const cfModelId = framework.getModelId()
  const {
    controlOptions,
    controls,
    isLoading: controlsLoading,
  } = useFrameworkControls(show)
  const [sectionDraft, setSectionDraft] = useState<SectionDraft[]>([])
  const [editErrors, setEditErrors] = useState<FrameworkSectionsEditorErrors>(
    {},
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!show || !selectedSection) return
    setSectionDraft([selectedSection.sectionDraft])
    setEditErrors({})
    setIsSubmitting(false)
  }, [selectedSection, show])

  const otherSectionNames = useMemo(
    () =>
      (selectedSection?.sectionDraftsSnapshot ?? [])
        .filter((s) => s.tempId !== selectedSection?.sectionId)
        .map((s) => s.name),
    [selectedSection],
  )
  const otherRequirementNames = useMemo(
    () =>
      (selectedSection?.sectionDraftsSnapshot ?? [])
        .filter((s) => s.tempId !== selectedSection?.sectionId)
        .flatMap((s) => s.requirements)
        .map((r) => r.name),
    [selectedSection],
  )

  const errors: FrameworkSectionsEditorErrors = useMemo(
    () => ({
      sectionNames: editErrors.sectionNames,
      requirementNames: editErrors.requirementNames,
    }),
    [editErrors],
  )

  const closeAndReset = () => {
    hideModal()
    setSectionDraft([])
    setEditErrors({})
    setIsSubmitting(false)
    clearSelectedSection()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!selectedSection || sectionDraft.length === 0) return
    setEditErrors({})

    const empty = getEmptyNameErrors(sectionDraft)
    const dupes = getDuplicateNameErrors(
      sectionDraft,
      otherSectionNames,
      otherRequirementNames,
    )
    const sectionNames = { ...empty.sectionNames, ...dupes.sectionNames }
    const requirementNames = {
      ...empty.requirementNames,
      ...dupes.requirementNames,
    }
    if (
      Object.keys(sectionNames).length ||
      Object.keys(requirementNames).length
    ) {
      setEditErrors({ sectionNames, requirementNames })
      return
    }

    setIsSubmitting(true)
    try {
      await saveSections({
        complianceFrameworkModelId: cfModelId,
        originalSections: selectedSection.sectionDraftsSnapshot,
        newSections: selectedSection.sectionDraftsSnapshot.map(
          (snapshotSection) =>
            snapshotSection.tempId === selectedSection.sectionId
              ? sectionDraft[0]
              : snapshotSection,
        ),
        controls,
      })
      toast.success('Section updated.')
      closeAndReset()
    } catch (err) {
      const dupResult = parseBackendDuplicateError(err, sectionDraft)
      if (dupResult) {
        if (dupResult.editorErrors) setEditErrors(dupResult.editorErrors)
        if (dupResult.toast) toast.error(dupResult.toast)
        return
      }
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('EditFrameworkSectionModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.EDIT_FRAMEWORK_SECTION}
      size="xl"
      title="Edit Section"
      submitText="Save"
      showOverflow
      disableSubmitButton={
        isSubmitting ||
        controlsLoading ||
        !selectedSection ||
        sectionDraft.length === 0
      }
    >
      <form id={ModalFormId.EDIT_FRAMEWORK_SECTION} onSubmit={onSubmit}>
        <FrameworkSectionsEditor
          sections={sectionDraft}
          setSections={setSectionDraft}
          setErrors={setEditErrors}
          controlOptions={controlOptions}
          errors={errors}
          disableSectionCrud
        />
      </form>
    </ModalForm>
  )
}
